import Text from '@creator/ui/components/Text/Text';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { getDomainConfigValue } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import type { FC } from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChooseLoginUser from '../ChooseLoginUser/ChooseLoginUser';
import MergeAccount from '../MergeAccount/MergeAccount';

type Step = 'chooseLoginUser' | 'mergeAccounts' | 'success';

export interface FirebaseAuthProps {
    className?: string;
    initialStep?: Step;
}

const MergeAccountWrapper: FC<FirebaseAuthProps> = props => {
    const { initialStep = 'chooseLoginUser' } = props;

    const { t } = useTranslation();

    const { handleModal } = useContext<UseModal>(ModalContext);

    const myUser = useStoreState(state => state.user.myUser);
    const myLoginUsers = useStoreState(state => state.user.myLoginUsers);
    const loginUsers = myLoginUsers ? myLoginUsers.filter(user => !user.forwardTo || user.mainAccount) : [];

    const [step, setStep] = useState<Step>(initialStep);

    function onMergeAccountsSubmit(): void {
        handleModal(null);
    }

    function onCancel(): void {
        handleModal(null);
    }

    function onBack(): void {
        setStep('chooseLoginUser');
    }

    function getHeaderTitle(): string {
        if (step === 'chooseLoginUser') return t('mergeAccountWrapper.title');
        if (step === 'mergeAccounts') return t('mergeAccountWrapper.title');

        return t('authModalTitle', { displayName: getDomainConfigValue('domainDisplayName') });
    }

    function getHeaderSubTitle(): string {
        if (step === 'mergeAccounts') return t('mergeAccountWrapper.subtitle');
        if (step === 'chooseLoginUser') return t('mergeAccountWrapper.subtitle');
        return t('authModalSubtitle');
    }

    function renderHeader() {
        return (
            <div className="mb-4 text-center">
                <Text enableMultiline={true} className="block text-2xl font-bold text-black transition dark:text-gray-50">
                    {getHeaderTitle()}
                </Text>
                {step === 'chooseLoginUser' && (
                    <Text enableMultiline={true} className="block mt-2 font-medium text-gray-500">
                        {getHeaderSubTitle()}
                    </Text>
                )}
            </div>
        );
    }

    function renderChooseLoginUser() {
        if (step !== 'chooseLoginUser' || !loginUsers.length || !myUser) return null;
        return (
            <ChooseLoginUser
                onSubmit={onCancel}
                submitButtonText={t('mergeAccountWrapper.cancelButton')}
                alertBoxText={t('mergeAccountWrapper.alertBox')}
                initialSelectedUser={myUser}
                users={loginUsers}
                onMergeAccountClick={() => setStep('mergeAccounts')}
            />
        );
    }

    function renderMergeAccounts() {
        if (step !== 'mergeAccounts' || !loginUsers.length || !myUser) return null;
        return (
            <MergeAccount
                cancelButtonText={t('mergeAccountWrapper.backButton')}
                initialSelectedUser={myUser}
                users={loginUsers}
                onCancelClick={onBack}
                onSuccess={onMergeAccountsSubmit}
            />
        );
    }

    function renderContent() {
        if (step === 'chooseLoginUser') return renderChooseLoginUser();
        return renderMergeAccounts();
    }

    return (
        <div>
            {renderHeader()}
            {renderContent()}
        </div>
    );
};

export default MergeAccountWrapper;
